import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { fileUrl } from '../utils/fileUrl';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Users, CheckCircle, XCircle, Gift, Trash2, UserPlus, Loader2, FileText, Upload, Phone, Car, Star, Ban, Eye } from 'lucide-react';

const DocumentRow = ({ label, url, fileType, driverId, onUploaded, accept }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const isPdf = url && url.toLowerCase().endsWith('.pdf');

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/admin/drivers/${driverId}/documents?file_type=${fileType}`, formData);
      toast.success(`${label} uploaded`);
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to upload ${label}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 border rounded-lg p-3" data-testid={`doc-row-${fileType}`}>
      <div className="flex items-center gap-3 min-w-0">
        {url ? <CheckCircle className="w-5 h-5 text-green-600 shrink-0" /> : <XCircle className="w-5 h-5 text-red-500 shrink-0" />}
        <div className="min-w-0">
          <p className="font-medium text-sm">{label}</p>
          <p className={`text-xs ${url ? 'text-green-600' : 'text-red-500'}`}>{url ? 'Completed' : 'Missing'}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {url && (
          <a href={fileUrl(url)} target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline" data-testid={`view-doc-${fileType}`}>
              {isPdf ? <FileText className="w-3.5 h-3.5 mr-1" /> : <Eye className="w-3.5 h-3.5 mr-1" />} View
            </Button>
          </a>
        )}
        <Button size="sm" variant="outline" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid={`upload-doc-${fileType}`}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
          {url ? 'Replace' : 'Upload'}
        </Button>
        <input ref={inputRef} type="file" className="hidden" accept={accept} onChange={handleFile} />
      </div>
    </div>
  );
};

const docsCount = (d) => [d.profile_photo_url, d.chauffeur_license_url, d.insurance_doc_url].filter(Boolean).length;

const AdminDriverManagement = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileDriver, setProfileDriver] = useState(null);
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [addingDriver, setAddingDriver] = useState(false);
  const [newDriver, setNewDriver] = useState({ name: '', email: '', phone: '', car_type: '', license_plate: '', send_welcome_email: true });
  const [showGrantAccess, setShowGrantAccess] = useState(false);
  const [grantDriverId, setGrantDriverId] = useState(null);
  const [freeAccessMonths, setFreeAccessMonths] = useState('');
  const [unlimitedAccess, setUnlimitedAccess] = useState(true);

  useEffect(() => { fetchDrivers(); }, []);

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API}/admin/drivers`);
      setDrivers(response.data);
    } catch (error) {
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  const openProfile = async (driverId) => {
    try {
      const res = await axios.get(`${API}/admin/drivers/${driverId}/profile`);
      setProfileDriver(res.data);
    } catch (e) {
      toast.error('Failed to load driver profile');
    }
  };

  const refreshProfile = async () => {
    if (profileDriver) {
      await openProfile(profileDriver.id);
      fetchDrivers();
    }
  };

  const handleAddDriver = async (e) => {
    e.preventDefault();
    setAddingDriver(true);
    try {
      const res = await axios.post(`${API}/admin/drivers`, newDriver);
      toast.success(newDriver.send_welcome_email && res.data.email_sent
        ? `Driver added — welcome email with credentials sent to ${newDriver.email}`
        : 'Driver added (default password: Driver123!, must change on first login)');
      setShowAddDriver(false);
      setNewDriver({ name: '', email: '', phone: '', car_type: '', license_plate: '', send_welcome_email: true });
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add driver');
    } finally {
      setAddingDriver(false);
    }
  };

  const toggleActive = async (driver) => {
    const deactivating = driver.is_active !== false;
    if (deactivating && !window.confirm(`Deactivate ${driver.name}? They will not be able to log in until reactivated.`)) return;
    try {
      await axios.put(`${API}/admin/drivers/${driver.id}/status`, { is_active: !deactivating });
      toast.success(deactivating ? `${driver.name} deactivated` : `${driver.name} activated`);
      fetchDrivers();
      if (profileDriver?.id === driver.id) openProfile(driver.id);
    } catch (e) {
      toast.error('Failed to update driver status');
    }
  };

  const submitGrantAccess = async () => {
    try {
      await axios.post(`${API}/admin/grant-free-access`, {
        driver_id: grantDriverId,
        unlimited: unlimitedAccess,
        months: unlimitedAccess ? null : parseInt(freeAccessMonths)
      });
      toast.success('Free access granted successfully');
      setShowGrantAccess(false);
      setGrantDriverId(null);
      setFreeAccessMonths('');
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    }
  };

  const handleRevokeAccess = async (driverId) => {
    if (!window.confirm('Are you sure you want to revoke free access?')) return;
    try {
      await axios.post(`${API}/admin/revoke-free-access/${driverId}`);
      toast.success('Free access revoked');
      fetchDrivers();
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const handleDeleteDriver = async (driver) => {
    if (!window.confirm(`Are you sure you want to permanently delete driver "${driver.name}"?\n\nThis cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/drivers/${driver.id}`);
      toast.success(`Driver "${driver.name}" deleted`);
      setProfileDriver(null);
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete driver');
    }
  };

  if (loading) {
    return <div className="max-w-6xl mx-auto"><Card><CardContent className="pt-6"><div className="text-center py-8">Loading drivers...</div></CardContent></Card></div>;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Users className="w-8 h-8" /> Driver Management
        </h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600">Total Drivers: {drivers.length}</span>
          <Button onClick={() => setShowAddDriver(!showAddDriver)} data-testid="add-driver-button">
            <UserPlus className="w-4 h-4 mr-2" /> Add Driver
          </Button>
        </div>
      </div>

      {/* Add Driver Form */}
      {showAddDriver && (
        <Card className="mb-6 border-2 border-black">
          <CardHeader><CardTitle>Add New Driver</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleAddDriver} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input data-testid="new-driver-name" required value={newDriver.name} onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })} placeholder="John Smith" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input data-testid="new-driver-email" required type="email" value={newDriver.email} onChange={(e) => setNewDriver({ ...newDriver, email: e.target.value })} placeholder="driver@email.com" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input data-testid="new-driver-phone" value={newDriver.phone} onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })} placeholder="(305) 555-0123" />
                </div>
                <div>
                  <Label>Vehicle (e.g. 2026 Cadillac Escalade)</Label>
                  <Input data-testid="new-driver-vehicle" value={newDriver.car_type} onChange={(e) => setNewDriver({ ...newDriver, car_type: e.target.value })} placeholder="2026 Chevrolet Suburban" />
                </div>
                <div>
                  <Label>License Plate</Label>
                  <Input data-testid="new-driver-plate" value={newDriver.license_plate} onChange={(e) => setNewDriver({ ...newDriver, license_plate: e.target.value })} placeholder="ABC 1234" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={newDriver.send_welcome_email} onChange={(e) => setNewDriver({ ...newDriver, send_welcome_email: e.target.checked })} data-testid="new-driver-send-email" />
                Email login credentials to the driver (default password: Driver123!, they must change it on first login)
              </label>
              <div className="flex gap-2">
                <Button type="submit" disabled={addingDriver} data-testid="new-driver-submit">
                  {addingDriver ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />} Create Driver
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddDriver(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Grant Access Modal */}
      {showGrantAccess && (
        <Card className="mb-6 border-2 border-blue-500">
          <CardHeader><CardTitle>Grant Free Access</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input type="radio" checked={unlimitedAccess} onChange={() => setUnlimitedAccess(true)} />
                  <span>Unlimited Access</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" checked={!unlimitedAccess} onChange={() => setUnlimitedAccess(false)} />
                  <span>Time-Limited Access</span>
                </label>
              </div>
              {!unlimitedAccess && (
                <div>
                  <Label htmlFor="months">Number of Months</Label>
                  <Input id="months" type="number" min="1" value={freeAccessMonths} onChange={(e) => setFreeAccessMonths(e.target.value)} placeholder="Enter number of months" />
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={submitGrantAccess} className="flex-1">Confirm Grant Access</Button>
                <Button variant="outline" onClick={() => { setShowGrantAccess(false); setGrantDriverId(null); setFreeAccessMonths(''); }} className="flex-1">Cancel</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Drivers Table */}
      <Card>
        <CardHeader><CardTitle>All Drivers <span className="text-sm font-normal text-gray-500">— click a driver to view their profile & documents</span></CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documents</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscription</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {drivers.map((driver) => {
                  const dc = docsCount(driver);
                  const deactivated = driver.is_active === false;
                  return (
                    <tr key={driver.id} className={`hover:bg-gray-50 cursor-pointer ${deactivated ? 'opacity-60' : ''}`} onClick={() => openProfile(driver.id)} data-testid={`driver-row-${driver.id}`}>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          {driver.profile_photo_url ? (
                            <img src={fileUrl(driver.profile_photo_url)} alt={driver.name} className="w-9 h-9 rounded-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm font-bold">{driver.name?.[0]}</div>
                          )}
                          <div>
                            <div className="font-medium">{driver.name}</div>
                            <div className="text-sm text-gray-500">{driver.car_type}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm">{driver.email}</td>
                      <td className="px-4 py-4">
                        {deactivated ? (
                          <span className="flex items-center gap-1 text-red-600 text-sm"><Ban className="w-4 h-4" /> Deactivated</span>
                        ) : (
                          <span className="flex items-center gap-1 text-green-600 text-sm"><CheckCircle className="w-4 h-4" /> Active</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`text-sm font-medium ${dc === 3 ? 'text-green-600' : 'text-gold-600'}`} data-testid={`docs-count-${driver.id}`}>
                          {dc === 3 ? '✓ Complete' : `${dc}/3`}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {driver.free_access_granted ? (
                          <span className="flex items-center gap-1 text-blue-600 text-sm"><Gift className="w-4 h-4" /> Free Access</span>
                        ) : (
                          <span className="text-sm text-gray-600">{driver.subscription_status}</span>
                        )}
                      </td>
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant={deactivated ? 'default' : 'outline'} onClick={() => toggleActive(driver)} data-testid={`toggle-active-${driver.id}`}>
                            {deactivated ? 'Activate' : 'Deactivate'}
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteDriver(driver)} data-testid={`delete-driver-${driver.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Driver Profile Dialog */}
      <Dialog open={!!profileDriver} onOpenChange={(open) => !open && setProfileDriver(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="driver-profile-dialog">
          {profileDriver && (
            <>
              <DialogHeader>
                <DialogTitle>Driver Profile</DialogTitle>
              </DialogHeader>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  {profileDriver.profile_photo_url ? (
                    <img src={fileUrl(profileDriver.profile_photo_url)} alt={profileDriver.name} className="w-20 h-20 rounded-full object-cover border" onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-2xl font-bold">{profileDriver.name?.[0]}</div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-xl font-bold">{profileDriver.name}</h2>
                      {profileDriver.is_active === false ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Deactivated</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>
                      )}
                      {profileDriver.documents_complete ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700" data-testid="docs-complete-badge">✓ All Documents Complete</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gold-100 text-gold-700" data-testid="docs-incomplete-badge">Documents Incomplete</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{profileDriver.email}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {profileDriver.phone || 'No phone on file'}</p>
                    <p className="text-sm text-gray-600 flex items-center gap-1 mt-1"><Car className="w-3 h-3" /> {profileDriver.car_type || 'No vehicle'} {profileDriver.license_plate ? `• ${profileDriver.license_plate}` : ''}</p>
                    {profileDriver.total_ratings > 0 && (
                      <p className="text-sm text-gray-600 flex items-center gap-1 mt-1"><Star className="w-3 h-3 text-gold-500" /> {profileDriver.average_rating?.toFixed(1)} ({profileDriver.total_ratings} ratings)</p>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2"><FileText className="w-4 h-4" /> Documents</h3>
                  <div className="space-y-2">
                    <DocumentRow label="Profile Photo" url={profileDriver.profile_photo_url} fileType="profile" driverId={profileDriver.id} onUploaded={refreshProfile} accept="image/*" />
                    <DocumentRow label="Chauffeur's License" url={profileDriver.chauffeur_license_url} fileType="license" driverId={profileDriver.id} onUploaded={refreshProfile} accept="image/*,application/pdf" />
                    <DocumentRow label="Insurance Document" url={profileDriver.insurance_doc_url} fileType="insurance" driverId={profileDriver.id} onUploaded={refreshProfile} accept="image/*,application/pdf" />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  <Button variant={profileDriver.is_active === false ? 'default' : 'outline'} onClick={() => toggleActive(profileDriver)} data-testid="profile-toggle-active">
                    {profileDriver.is_active === false ? <><CheckCircle className="w-4 h-4 mr-2" /> Activate Driver</> : <><Ban className="w-4 h-4 mr-2" /> Deactivate Driver</>}
                  </Button>
                  {profileDriver.free_access_granted ? (
                    <Button variant="outline" onClick={() => { handleRevokeAccess(profileDriver.id); setProfileDriver(null); }}>Revoke Free Access</Button>
                  ) : (
                    <Button variant="outline" onClick={() => { setGrantDriverId(profileDriver.id); setShowGrantAccess(true); setProfileDriver(null); }}>Grant Free Access</Button>
                  )}
                  <Button variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteDriver(profileDriver)}>
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Driver
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDriverManagement;
